<template>
    <GoogleMap id="map" ref="GMap" :api-key="apiKey"
               style="width: 100%; height: 100%;"
               :scale-control="true" @idle="setupZoomControls"
               :center="center" :zoom="12" :streetViewControl="false" :fullscreenControl="false"
               :mapTypeControl="false" :styles="styles" :clickableIcons="false">
        <GPolygon v-for="area in areas" :options="area" :key="hashArea(area)"/>
    </GoogleMap>
</template>

<script>
    import {
        getPublicAreas,
    } from "@/swagger/vue-api-client";
    import {
        GoogleMap, Polygon as GPolygon,
    } from "vue3-google-map";
    import {MapUtils} from "@/utils/MapUtils";
    import hash from "object-hash";
    import svgPlus from "@/components/form/svg/plus.svg";
    import svgMinus from "@/components/form/svg/minus.svg";

    export default {
        name: "EonMap",

        props: {
            searchCriteria: null,
            showMarkersOnMap: {
                default: true,
            },
            showAreaOutlines: {
                default: true,
            },
            centerProp: {
                default: {lat: 52.232222, lng: 21.008333},
            },
        },

        data() {
            return {
                circleOptions: MapUtils.createCircleOptions(),
                userClusterOptions: {renderer: MapUtils.userReportsClusterRenderer},
                markerActive: false,
                activeAreaId: null,
                allowCircleRender: true,
                areas: null,
                center: {lat: 52.232222, lng: 21.008333},
                apiKey: process.env.VUE_APP_GOOGLE_API_KEY,
                styles: [
                    {
                        elementType: "labels.text.fill",
                        stylers: [
                            {
                                color: "#9d9d9d",
                            },
                        ],
                    },
                    {
                        featureType: "landscape",
                        elementType: "geometry",
                        stylers: [
                            {
                                color: "#f6f6f4",
                            },
                        ],
                    },
                    {
                        featureType: "poi",
                        stylers: [
                            {
                                visibility: "off",
                            },
                        ],
                    },
                    {
                        featureType: "road",
                        elementType: "geometry",
                        stylers: [
                            {
                                color: "#fdfdfd",
                            },
                        ],
                    },
                    {
                        featureType: "road",
                        elementType: "labels",
                        stylers: [
                            {
                                visibility: "simplified",
                            },
                        ],
                    },
                    {
                        featureType: "road",
                        elementType: "labels.icon",
                        stylers: [
                            {
                                visibility: "off",
                            },
                        ],
                    },
                    {
                        featureType: "transit",
                        stylers: [
                            {
                                visibility: "off",
                            },
                        ],
                    },
                    {
                        featureType: "water",
                        elementType: "geometry",
                        stylers: [
                            {
                                color: "#cad2d3",
                            },
                        ],
                    },
                    {
                        featureType: "water",
                        elementType: "labels.text.fill",
                        stylers: [
                            {
                                color: "#7a7a7a",
                            },
                        ],
                    },
                ],
            };
        },
        async mounted() {
            await this.setupZoomControls();
        },

        async beforeMount() {
            this.center = this.centerProp;
            await getPublicAreas().then((response) => {
                if (this.showAreaOutlines) {
                    this.areas = MapUtils.adjustAreaOutlines(response.data);
                } else {
                    this.areas = MapUtils.adjustAreas(response.data);
                }
            });
        },

        methods: {
            hashArea(area) {
                return hash(area);
            },
            toggleVisibility(areaId) {
                const area = this.areas.find((el) => el.id === areaId);
                area.visible = !area.visible;
            },
            async setupZoomControls() {
                await setTimeout(() => {
                    Array.from(document.getElementsByClassName("gm-control-active")).forEach((button) => {
                        // Usunięcie tła.
                        button.parentElement.style.boxShadow = "";
                        button.parentElement.style.backgroundColor = "";

                        // Zmiana plusa/minusa na ten z figmy.
                        if (button.title === "Zoom in" || button.title === "Powiększ") {
                            Array.from(button.children).forEach((child) => {
                                child.src = svgPlus;
                            });
                        } else if (button.title === "Zoom out" || button.title === "Pomniejsz") {
                            Array.from(button.children).forEach((child) => {
                                child.src = svgMinus;
                            });
                        }
                        const iconSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
                        const iconPath = document.createElementNS(
                            "http://www.w3.org/2000/svg",
                            "path",
                        );

                        if (button.getAttribute("aria-label") === "Zoom in"
                            || button.getAttribute("aria-label") === "Powiększ") {
                            button.style.setProperty("top", "-4px");
                        }

                        iconSvg.setAttribute("fill", "none");
                        iconSvg.setAttribute("viewBox", "0 0 48 48");
                        iconSvg.setAttribute("width", "40");
                        iconSvg.setAttribute("height", "40");
                        iconPath.setAttribute(
                            "d",
                            // eslint-disable-next-line max-len
                            "M7.76825 1.544C6.22594 1.83684 4.80747 2.5871 3.69741 3.69716C2.58735 4.80723 1.83709 6.22569 1.54425 7.768C-0.51475 18.589 -0.51475 29.411 1.54425 40.232C1.83709 41.7743 2.58735 43.1928 3.69741 44.3028C4.80747 45.4129 6.22594 46.1632 7.76825 46.456C13.1184 47.4794 18.5531 47.9964 24.0002 48C29.4474 47.9964 34.8821 47.4794 40.2323 46.456C41.7746 46.1632 43.193 45.4129 44.3031 44.3028C45.4132 43.1928 46.1634 41.7743 46.4563 40.232C48.5153 29.411 48.5153 18.589 46.4563 7.768C46.1634 6.22569 45.4132 4.80723 44.3031 3.69716C43.193 2.5871 41.7746 1.83684 40.2323 1.544C34.8821 0.520449 29.4474 0.00349601 24.0002 0C18.5531 0.00349601 13.1184 0.520449 7.76825 1.544Z",
                        );
                        iconPath.setAttribute("clip-rule", "evenodd");
                        iconPath.setAttribute("fill-rule", "evenodd");
                        iconPath.setAttribute("fill", "#EA1B0A");
                        iconSvg.appendChild(iconPath);
                        button.appendChild(iconSvg);
                    });
                }, 2000);
            },
        },

        components: {
            GoogleMap,
            GPolygon,
        },
    };
</script>
<style lang="less">
@import "../assets/theme/variable.less";
.marker {
    width: 25px;
}
.gm-style div {
    border: none !important;
}
.gm-style .gm-style-iw-c {
    border-radius: 4px;
    padding: 0;
}
.gm-style-iw-d {
    overflow: hidden !important;
    text-align: left;
}
.gm-ui-hover-effect {
    top: 3px !important;
    right: 5px !important;
}

</style>
